import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import NotifiedDriversButton from "@/components/admin/NotifiedDriversButton";
import { toast } from "sonner";
import {
  Search, Filter, Loader2, MapPin, Users, Euro, Star, Clock,
  Car, RadioTower, ChevronDown, ChevronUp, Ban, CheckCircle2,
} from "lucide-react";

/**
 * Admin — History Tab.
 *
 * Vue COMPLÈTE de l'historique de la plateforme :
 *   - Toutes les demandes live (pending, broadcasting, accepted,
 *     passenger_boarding, in_progress, completed, cancelled, expired).
 *   - Tous les trajets publiés par les covoitureurs (tous statuts).
 *
 * Utilise GET /api/admin/price/rides/history avec filtres :
 *   - q : recherche par email/nom/ID/adresse
 *   - status_filter : filtre par statut
 *   - kind : "live" | "trip" | "" (tout)
 */

const STATUS_LABEL = {
  pending: "En attente",
  pending_payment: "Paiement",
  broadcasting: "Recherche",
  accepted: "Accepté",
  passenger_boarding: "Embarquement",
  in_progress: "En cours",
  completed: "Terminé",
  cancelled: "Annulé",
  expired: "Expiré",
  open: "Ouvert",
  full: "Complet",
  closed: "Fermé",
};

const STATUS_TONE = {
  completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  expired: "bg-neutral-100 text-neutral-600 border-neutral-300",
  pending: "bg-amber-50 text-amber-700 border-amber-200",
  pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
  broadcasting: "bg-blue-50 text-blue-700 border-blue-200",
  accepted: "bg-blue-50 text-blue-700 border-blue-200",
  passenger_boarding: "bg-blue-50 text-blue-700 border-blue-200",
  in_progress: "bg-blue-50 text-blue-700 border-blue-200",
  open: "bg-emerald-50 text-emerald-700 border-emerald-200",
  full: "bg-blue-50 text-blue-700 border-blue-200",
  closed: "bg-neutral-100 text-neutral-600 border-neutral-300",
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function StatusBadge({ status }) {
  const tone = STATUS_TONE[status] || "bg-neutral-100 text-neutral-700 border-neutral-300";
  return (
    <span className={`inline-block text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border ${tone}`}>
      {STATUS_LABEL[status] || status || "—"}
    </span>
  );
}

function LiveRow({ r }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl border-2 border-black bg-white p-3 shadow-[0_3px_0_0_#000]"
      data-testid={`admin-history-live-${r.id?.slice(0, 8)}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={r.status} />
          <span className="text-[10px] font-bold text-neutral-500 uppercase">Live · {r.id?.slice(0, 6)}</span>
        </div>
        <div className="flex items-center gap-1 font-black text-[14px]">
          <Euro className="w-3.5 h-3.5" strokeWidth={3} />
          {Number(r.price_eur || 0).toFixed(2)}
        </div>
      </div>
      <NotifiedDriversButton requestId={r.id} />
      <div className="space-y-0.5 text-[12px]">
        <div className="flex items-start gap-1.5">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#FF3B3B]" strokeWidth={3} />
          <span className="truncate">{r.from_address || "—"}</span>
        </div>
        <div className="flex items-start gap-1.5 text-neutral-600">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-neutral-400" strokeWidth={3} />
          <span className="truncate">{r.to_address || "—"}</span>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-neutral-200 flex items-center justify-between">
        <div className="text-[11px] text-neutral-600">
          <Clock className="w-3 h-3 inline mr-1" strokeWidth={3} />
          {fmtDate(r.created_at)}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-black underline text-neutral-700"
          data-testid={`admin-history-toggle-${r.id?.slice(0, 8)}`}
        >
          {open ? <ChevronUp className="w-3.5 h-3.5 inline" /> : <ChevronDown className="w-3.5 h-3.5 inline" />}
          Détails
        </button>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-neutral-200 space-y-1 text-[11px]">
          <div>
            <span className="font-bold text-neutral-500">Passager :</span>{" "}
            <span>{r.client_name || "—"}</span>
            {r.client_stars > 0 && <span className="ml-1">· ⭐ {r.client_stars}/5</span>}
          </div>
          <div>
            <span className="font-bold text-neutral-500">Covoitureur :</span>{" "}
            <span>{r.driver_name || "—"}</span>
            {r.driver_stars > 0 && <span className="ml-1">· ⭐ {r.driver_stars}/5</span>}
          </div>
          {r.seats > 0 && (
            <div>
              <span className="font-bold text-neutral-500">Places :</span> {r.seats}
            </div>
          )}
          {r.distance_km != null && (
            <div>
              <span className="font-bold text-neutral-500">Distance :</span> {Number(r.distance_km).toFixed(1)} km
            </div>
          )}
          {r.payment_method && (
            <div>
              <span className="font-bold text-neutral-500">Paiement :</span> {r.payment_method}
            </div>
          )}
          {r.completed_at && (
            <div>
              <span className="font-bold text-neutral-500">Terminé le :</span> {fmtDate(r.completed_at)}
            </div>
          )}
          {r.cancelled_at && (
            <div className="text-rose-600">
              <span className="font-bold">Annulé le :</span> {fmtDate(r.cancelled_at)}
              {r.cancelled_by && <span> · par {r.cancelled_by}</span>}
              {r.cancel_reason && <div className="italic">Raison : {r.cancel_reason}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TripRow({ t }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="rounded-2xl border-2 border-black bg-white p-3 shadow-[0_3px_0_0_#000]"
      data-testid={`admin-history-trip-${t.id?.slice(0, 8)}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <StatusBadge status={t.status} />
          <span className="text-[10px] font-bold text-neutral-500 uppercase">Trajet · {t.id?.slice(0, 6)}</span>
        </div>
        <div className="flex items-center gap-1 font-black text-[14px]">
          <Euro className="w-3.5 h-3.5" strokeWidth={3} />
          {Number(t.price_eur || 0).toFixed(2)}
        </div>
      </div>
      <div className="space-y-0.5 text-[12px]">
        <div className="flex items-start gap-1.5">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-[#FF3B3B]" strokeWidth={3} />
          <span className="truncate">{t.from_address || "—"}</span>
        </div>
        <div className="flex items-start gap-1.5 text-neutral-600">
          <MapPin className="w-3 h-3 mt-0.5 shrink-0 text-neutral-400" strokeWidth={3} />
          <span className="truncate">{t.to_address || "—"}</span>
        </div>
      </div>
      <div className="mt-2 pt-2 border-t border-neutral-200 flex items-center justify-between">
        <div className="text-[11px] text-neutral-600">
          <Clock className="w-3 h-3 inline mr-1" strokeWidth={3} />
          {t.departure_date ? `${t.departure_date} ${t.departure_time || ""}` : fmtDate(t.created_at)}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-bold inline-flex items-center gap-0.5">
            <Users className="w-3 h-3" strokeWidth={3} /> {t.seats_taken || 0}/{t.seats_total || 0}
          </span>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[11px] font-black underline text-neutral-700"
            data-testid={`admin-history-trip-toggle-${t.id?.slice(0, 8)}`}
          >
            {open ? <ChevronUp className="w-3.5 h-3.5 inline" /> : <ChevronDown className="w-3.5 h-3.5 inline" />}
            Détails
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-2 pt-2 border-t border-neutral-200 space-y-1 text-[11px]">
          <div>
            <span className="font-bold text-neutral-500">Covoitureur :</span>{" "}
            <span>{t.driver_name || "—"}</span>
          </div>
          {t.completed_at && (
            <div>
              <span className="font-bold text-neutral-500">Terminé le :</span> {fmtDate(t.completed_at)}
            </div>
          )}
          {t.cancelled_at && (
            <div className="text-rose-600">
              <span className="font-bold">Annulé le :</span> {fmtDate(t.cancelled_at)}
            </div>
          )}
          {Array.isArray(t.booked_seats) && t.booked_seats.length > 0 && (
            <div>
              <div className="font-bold text-neutral-500 mb-0.5">Passagers ({t.booked_seats.length}) :</div>
              <ul className="space-y-0.5">
                {t.booked_seats.map((s) => (
                  <li key={s.id} className="flex items-center gap-1">
                    <span>{s.client_name || "Passager"}</span>
                    <span className="text-neutral-500">· {s.seats || 1} pl</span>
                    <StatusBadge status={s.status} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function HistoryTab() {
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kind, setKind] = useState(""); // "" | "live" | "trip"
  const [live, setLive] = useState([]);
  const [trips, setTrips] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (statusFilter) params.set("status_filter", statusFilter);
      if (kind) params.set("kind", kind);
      params.set("limit", "200");
      const r = await api.get(`/admin/price/rides/history?${params.toString()}`);
      setLive(Array.isArray(r.data?.live_requests) ? r.data.live_requests : []);
      setTrips(Array.isArray(r.data?.published_trips) ? r.data.published_trips : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter, kind]);

  useEffect(() => { load(); }, [load]);

  const totalCount = live.length + trips.length;

  const statsByStatus = useMemo(() => {
    const s = {};
    for (const r of live) {
      s[r.status] = (s[r.status] || 0) + 1;
    }
    return s;
  }, [live]);

  const revenueCompleted = useMemo(() => {
    return live
      .filter((r) => r.status === "completed")
      .reduce((acc, r) => acc + Number(r.price_eur || 0), 0);
  }, [live]);

  return (
    <div className="space-y-4" data-testid="admin-history-tab">
      {/* Header */}
      <div className="rounded-2xl border-2 border-black bg-white p-4 shadow-[0_3px_0_0_#000]">
        <div className="flex items-center gap-2 mb-2">
          <RadioTower className="w-4 h-4" strokeWidth={3} />
          <h2 className="font-[Outfit] font-black text-[15px]">Historique complet</h2>
        </div>
        <p className="text-[12px] text-neutral-600 mb-3">
          Visibilité totale sur toutes les demandes et trajets publiés. Filtre par statut ou recherche par email, nom, ID ou adresse.
        </p>

        {/* Search + filter */}
        <div className="grid gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-400" strokeWidth={3} />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Recherche : email, nom, adresse, ID…"
              className="w-full rounded-xl border-2 border-black bg-white pl-8 pr-3 py-2 text-[13px] font-medium placeholder:text-neutral-400 focus:outline-none focus:ring-0"
              data-testid="admin-history-search"
            />
          </div>
          <div className="flex gap-2">
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="flex-1 rounded-xl border-2 border-black bg-white px-2 py-1.5 text-[12px] font-bold"
              data-testid="admin-history-kind"
            >
              <option value="">Tout (live + trajets)</option>
              <option value="live">Demandes live</option>
              <option value="trip">Trajets publiés</option>
            </select>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="flex-1 rounded-xl border-2 border-black bg-white px-2 py-1.5 text-[12px] font-bold"
              data-testid="admin-history-status"
            >
              <option value="">Tous statuts</option>
              <option value="pending">En attente</option>
              <option value="broadcasting">Recherche</option>
              <option value="accepted">Accepté</option>
              <option value="passenger_boarding">Embarquement</option>
              <option value="in_progress">En cours</option>
              <option value="completed">Terminé</option>
              <option value="cancelled">Annulé</option>
              <option value="expired">Expiré</option>
              <option value="open">Ouvert (trajet)</option>
              <option value="full">Complet (trajet)</option>
              <option value="closed">Fermé (trajet)</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        {!loading && (
          <div className="mt-3 pt-3 border-t border-neutral-200 grid grid-cols-3 gap-2 text-center">
            <div>
              <div className="text-[10px] font-bold text-neutral-500 uppercase">Total</div>
              <div className="font-[Outfit] font-black text-[16px]">{totalCount}</div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-neutral-500 uppercase">Terminées</div>
              <div className="font-[Outfit] font-black text-[16px] text-emerald-600">
                {statsByStatus.completed || 0}
              </div>
            </div>
            <div>
              <div className="text-[10px] font-bold text-neutral-500 uppercase">CA cumulé</div>
              <div className="font-[Outfit] font-black text-[16px]">
                {revenueCompleted.toFixed(2)}€
              </div>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-[#FF3B3B]" />
        </div>
      )}

      {/* Live requests */}
      {!loading && live.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Car className="w-4 h-4" strokeWidth={3} />
            <h3 className="font-[Outfit] font-black text-[13px] uppercase">
              Demandes live ({live.length})
            </h3>
          </div>
          {live.map((r) => (
            <LiveRow key={r.id} r={r} />
          ))}
        </div>
      )}

      {/* Trips */}
      {!loading && trips.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4" strokeWidth={3} />
            <h3 className="font-[Outfit] font-black text-[13px] uppercase">
              Trajets publiés ({trips.length})
            </h3>
          </div>
          {trips.map((t) => (
            <TripRow key={t.id} t={t} />
          ))}
        </div>
      )}

      {!loading && totalCount === 0 && (
        <div className="text-center py-10 rounded-2xl border-2 border-dashed border-neutral-300 bg-white">
          <Clock className="w-8 h-8 text-neutral-400 mx-auto mb-2" />
          <p className="text-[13px] text-neutral-600 font-bold">Aucun résultat pour ces filtres.</p>
        </div>
      )}
    </div>
  );
}
